import React, { useState, useEffect, useRef, Key } from "react"; // Ditambahkan: useRef
import { Alert, Spin, Table, Input, Button, Space } from 'antd'; // Ditambahkan: Input, Button, Space
import type { TableColumnsType, TableProps } from 'antd'; // Ditambahkan: ColumnType
import { SearchOutlined } from '@ant-design/icons'; // Ditambahkan: SearchOutlined
import type { InputRef } from 'antd'; // Ditambahkan: InputRef
import type { FilterConfirmProps, FilterDropdownProps } from 'antd/es/table/interface'; // Ditambahkan: FilterConfirmProps, FilterDropdownProps
import Highlighter from 'react-highlight-words'; // Ditambahkan: react-highlight-words (pastikan package ini terinstall: npm install react-highlight-words atau yarn add react-highlight-words)
import "./sp_kpp_code.css";

interface DataType {
    key: React.Key;
    kpp_code: string;
    kpp_name: string;
    status: string;
}

// Ditambahkan: Type untuk DataIndex
type DataIndex = keyof DataType;

interface SpKppCodeProps {
    onOk: (selectedData: { kpp_code: string; kpp_name: string }) => void;
}

const fetcher = async (url: string) => {
    const res = await fetch(url);
    if (!res.ok) {
        const error = new Error('Gagal mengambil data KPP');
        error.message = `Gagal mengambil data KPP: ${res.statusText}`;
        throw error;
    }
    return res.json();
};

type GetColumnSearchProps = (dataIndex: DataIndex) => NonNullable<NonNullable<TableProps<DataType>['columns']>[number]>


const SpKppCode: React.FC<SpKppCodeProps> = ({ onOk }) => {
    const transformStyle = {
        transform: "scale(0.80)",
        transformOrigin: "top left",
        width: "125%",
    };
    const [dataKpp, setDataKpp] = useState<DataType[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<Error | null>(null);
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    const apiUrlKpp = backendUrl ? `${backendUrl}/api/kpp` : null;

    // Ditambahkan: State untuk pencarian
    const [searchText, setSearchText] = useState('');
    const [searchedColumn, setSearchedColumn] = useState('');
    const searchInput = useRef<InputRef>(null);

    useEffect(() => {
        if (!apiUrlKpp) {
            setError(new Error("Backend URL tidak dikonfigurasi."));
            setLoading(false);
            return;
        }

        const fetchData = async () => {
            setLoading(true);
            setError(null);
            try {
                const data = await fetcher(apiUrlKpp);
                const processedData = data.map((item: any, index: number) => ({
                    ...item,
                    key: item.kpp_code || `kpp-${index}`
                }));
                setDataKpp(processedData);
            } catch (err) {
                console.error("Error fetching KPP data:", err);
                setError(err instanceof Error ? err : new Error('Terjadi error tidak dikenal saat fetch data KPP'));
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [apiUrlKpp]);

    // Ditambahkan: Fungsi untuk menangani pencarian
    const handleSearch = (
        selectedKeys: string[],
        confirm: (param?: FilterConfirmProps) => void,
        dataIndex: DataIndex,
    ) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
    };

    // Ditambahkan: Fungsi untuk mereset pencarian
    const handleReset = (clearFilters: () => void) => {
        clearFilters();
        setSearchText('');
        // Reset juga state searchedColumn jika diperlukan setelah reset
        // setSearchedColumn('');
    };

    // Ditambahkan: Fungsi untuk mendapatkan properti pencarian kolom
    const getColumnSearchProps: GetColumnSearchProps = (dataIndex) => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }: FilterDropdownProps) => (
            <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                <Input
                    ref={searchInput}
                    placeholder={`Cari ${dataIndex}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                    style={{ marginBottom: 8, display: 'block' }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Cari
                    </Button>
                    <Button
                        onClick={() => clearFilters && handleReset(clearFilters)}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Reset
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            close();
                        }}
                    >
                        Tutup
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered: boolean) => (
            <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
        ),
        onFilter: (value: boolean | Key, record: DataType) => 
            record[dataIndex]
                .toString()
                .toLowerCase()
                .includes(value.toString().toLowerCase()), // Diedit: Menggunakan value.toString() untuk keamanan tipe
        // Diedit: Menambahkan tipe eksplisit untuk visible
        onFilterDropdownonOpenChange: (visible: boolean) => {
            if (visible) {
                setTimeout(() => searchInput.current?.select(), 100);
            }
        },
        // Diedit: Menambahkan tipe eksplisit untuk text (bisa string atau tipe lain tergantung data)
        render: (text: any) => // Diedit: Menggunakan 'any' atau tipe spesifik jika diketahui (misal string)
            searchedColumn === dataIndex ? (
                <Highlighter
                    highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                    searchWords={[searchText]}
                    autoEscape
                    textToHighlight={text ? text.toString() : ''}
                />
            ) : (
                text
            ),
    });

    // Diedit: Menambahkan properti pencarian ke kolom
    const columns: TableColumnsType<DataType> = [
        {
            title: 'Code KPP',
            dataIndex: 'kpp_code',
            key: 'kpp_code',
            ...getColumnSearchProps('kpp_code'), // Ditambahkan
        },
        {
            title: 'KPP Name',
            dataIndex: 'kpp_name',
            key: 'kpp_name',
            ...getColumnSearchProps('kpp_name'), // Ditambahkan
        },
    ];

    const handleRowClick = (record: DataType) => {
        console.log("KPP Row selected:", record);
        onOk({ kpp_code: record.kpp_code, kpp_name: record.kpp_name });
    };

    if (loading) {
        return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}><Spin /></div>;
    }

    if (error) {
        const errorMessage = error instanceof Error ? error.message : 'Terjadi error tidak dikenal';
        return <Alert message="Error" description={`Gagal memuat data KPP: ${errorMessage}`} type="error" showIcon />;
    }

    return (
        <div id="iklk" style={transformStyle}>
            <div className="gjs-grid-row" id="i32vl">
                <div className="gjs-grid-column" id="iz8y8">
                    <Table
                        columns={columns}
                        dataSource={dataKpp}
                        rowKey="key"
                        onRow={(record) => {
                            return {
                                onDoubleClick: () => handleRowClick(record),
                            };
                        }}
                        size="small"
                        loading={loading}
                    />
                </div>
            </div>
        </div>
    );
};

export default SpKppCode;